/**
 * Capability catalog — single source of truth for the permission engine.
 *
 * Every yes/no authorization gate in the API maps to exactly one capability
 * (`domain.action`). `DEFAULT_ROLE_MATRIX` encodes today's hardcoded behavior
 * EXACTLY — the DB `role_permissions` table starts empty and only stores
 * overrides on top of these defaults.
 *
 * Row-level data scoping (scope.ts, dashboard my-day queries, etc.) is NOT
 * here — scoping stays role-based; this catalog is for gates only.
 */

import { USER_ROLES, type UserRole, type StaffRole } from '../reference/roles';

export interface CapabilityGroup {
  key: string;
  label: string;
}

export const CAPABILITY_GROUPS: CapabilityGroup[] = [
  { key: 'clients',     label: 'Clients' },
  { key: 'engagements', label: 'Engagements' },
  { key: 'proposals',   label: 'Proposals' },
  { key: 'compliance',  label: 'Compliance' },
  { key: 'time',        label: 'Time tracking' },
  { key: 'attendance',  label: 'Attendance' },
  { key: 'leave',       label: 'Leave' },
  { key: 'billing',     label: 'Billing' },
  { key: 'users',       label: 'Users & activity' },
  { key: 'tags',        label: 'Tags' },
  { key: 'goals',       label: 'Goals' },
  { key: 'automations', label: 'Automations' },
  { key: 'webhooks',    label: 'Webhooks' },
  { key: 'hub',         label: 'Hub' },
  { key: 'dashboard',   label: 'Dashboard' },
  { key: 'notes',       label: 'Notes' },
  { key: 'meetings',    label: 'Meetings' },
  { key: 'templates',   label: 'Templates' },
  { key: 'catalogs',    label: 'Catalogs & clauses' },
  { key: 'documents',   label: 'Documents' },
  { key: 'reports',     label: 'Reports' },
  { key: 'admin',       label: 'Administration' },
];

const CEO_ONLY: readonly StaffRole[] = ['CEO'];
const PRIVILEGED: readonly StaffRole[] = ['CEO', 'GM'];
const MANAGER_UP: readonly StaffRole[] = ['CEO', 'GM', 'Manager'];
const TEAMLEAD_UP: readonly StaffRole[] = ['CEO', 'GM', 'Manager', 'TeamLead'];
const ALL_STAFF: readonly StaffRole[] = ['CEO', 'GM', 'Manager', 'TeamLead', 'Staff'];

export interface CapabilityDef {
  key: string;
  label: string;
  description: string;
  group: string;
  /** Roles allowed by default — mirrors the pre-engine hardcoded checks. */
  roles: readonly StaffRole[];
}

export const CAPABILITIES: CapabilityDef[] = [
  // Clients (CRM)
  { key: 'clients.create',      label: 'Create clients',          description: 'Create clients manually and via bulk import',                    group: 'clients', roles: MANAGER_UP },
  { key: 'clients.edit',        label: 'Edit clients',            description: 'Edit client records',                                          group: 'clients', roles: MANAGER_UP },
  { key: 'clients.delete',      label: 'Delete clients',          description: 'Soft-delete individual clients',                               group: 'clients', roles: MANAGER_UP },
  { key: 'clients.bulk_delete', label: 'Bulk delete clients',     description: 'Bulk soft-delete clients (password re-auth still required)',   group: 'clients', roles: PRIVILEGED },

  // Engagements
  { key: 'engagements.create',  label: 'Create engagements',      description: 'Create new engagements',                                       group: 'engagements', roles: MANAGER_UP },
  { key: 'engagements.edit',    label: 'Edit engagements',        description: 'Edit engagements and their business setup checklists',              group: 'engagements', roles: MANAGER_UP },
  { key: 'engagements.delete',  label: 'Delete engagements',      description: 'Soft-delete engagements',                                      group: 'engagements', roles: MANAGER_UP },

  // Proposals
  { key: 'proposals.view',      label: 'View proposals',          description: 'List and read proposals',                                      group: 'proposals', roles: MANAGER_UP },
  { key: 'proposals.create',    label: 'Create proposals',        description: 'Create proposals on engagements',                              group: 'proposals', roles: MANAGER_UP },
  { key: 'proposals.edit',      label: 'Edit proposals',          description: 'Edit proposals and record a staff-side decline',               group: 'proposals', roles: MANAGER_UP },
  { key: 'proposals.send',      label: 'Send proposals',          description: 'Send proposals to clients for signing',                        group: 'proposals', roles: MANAGER_UP },
  { key: 'proposals.delete',    label: 'Delete proposals',        description: 'Soft-delete proposals (restorable via Admin Trash)',             group: 'proposals', roles: MANAGER_UP },

  // Compliance
  { key: 'compliance.manage',         label: 'Manage compliance clients', description: 'Create/edit/delete compliance clients, view the archive, list soft-deleted items', group: 'compliance', roles: MANAGER_UP },
  { key: 'compliance.edit',           label: 'Edit compliance items',     description: 'Create/edit/delete/import compliance items, view item archive, manage document requests', group: 'compliance', roles: TEAMLEAD_UP },
  { key: 'compliance.complete',       label: 'Complete items',            description: 'Mark a compliance item complete',                      group: 'compliance', roles: ALL_STAFF },
  { key: 'compliance.review',         label: 'Review & action items',     description: 'Run workflow actions (send back, send to client, recurrence) — Staff may only mark own items ready for review', group: 'compliance', roles: TEAMLEAD_UP },
  { key: 'compliance.bulk_delete',    label: 'Bulk delete items',         description: 'Bulk soft-delete compliance items (password re-auth still required)', group: 'compliance', roles: PRIVILEGED },
  { key: 'compliance.edit_completed', label: 'Edit completed items',      description: 'Edit or action items already marked Completed',        group: 'compliance', roles: PRIVILEGED },

  // Time tracking
  { key: 'time.log',         label: 'Log time',              description: 'Log time entries and run timers',                              group: 'time', roles: ALL_STAFF },
  { key: 'time.view_all',    label: 'View all time',         description: 'Filter time lists/exports by any user',                        group: 'time', roles: MANAGER_UP },
  { key: 'time.edit_others', label: "Edit others' time",     description: "Open/edit/delete other users' time entries and timers",        group: 'time', roles: MANAGER_UP },

  // Attendance
  { key: 'attendance.view_all', label: 'View all attendance', description: "View and export other users' attendance records",             group: 'attendance', roles: MANAGER_UP },
  { key: 'attendance.edit',     label: 'Edit attendance',     description: 'Correct attendance records',                                  group: 'attendance', roles: MANAGER_UP },

  // Leave
  { key: 'leave.request', label: 'Request leave',  description: 'Submit and cancel own leave requests',                                   group: 'leave', roles: ALL_STAFF },
  { key: 'leave.approve', label: 'Approve leave',  description: 'See the approvals queue and approve/reject leave requests',              group: 'leave', roles: MANAGER_UP },

  // Billing
  { key: 'billing.view',   label: 'View billing',   description: 'Invoices, WIP report, dashboard financials and proposal fees',          group: 'billing', roles: MANAGER_UP },
  { key: 'billing.manage', label: 'Manage billing', description: 'Create invoices, record payments, issue/send/cancel/void',              group: 'billing', roles: MANAGER_UP },

  // Users & activity
  { key: 'users.manage',  label: 'Manage users',   description: 'Create/edit/invite/disable users, reset passwords, see last-login times', group: 'users', roles: PRIVILEGED },
  { key: 'activity.view', label: 'View activity log', description: 'Read the firm-wide activity log API',                                 group: 'users', roles: PRIVILEGED },

  // Tags
  { key: 'tags.manage', label: 'Manage tags', description: 'Create/edit tags and assign/unassign them on entities',                       group: 'tags', roles: TEAMLEAD_UP },
  { key: 'tags.delete', label: 'Delete tags', description: 'Permanently delete tags',                                                     group: 'tags', roles: MANAGER_UP },

  // Goals
  { key: 'goals.manage', label: 'Manage goals for others', description: "Set and edit goals owned by other users",                        group: 'goals', roles: MANAGER_UP },
  { key: 'goals.delete', label: "Delete others' goals",    description: "Delete goals owned by other users",                              group: 'goals', roles: PRIVILEGED },

  // Automations
  { key: 'automations.manage', label: 'Manage automations', description: 'Create/edit/delete automation rules',                           group: 'automations', roles: MANAGER_UP },

  // Webhooks
  { key: 'webhooks.manage', label: 'Manage webhooks', description: 'Create/edit/test/delete webhook endpoints',                           group: 'webhooks', roles: PRIVILEGED },

  // Hub
  { key: 'hub.clear', label: 'Clear Hub chat', description: 'Wipe all Hub chat messages (activity feed is kept)',                         group: 'hub', roles: CEO_ONLY },

  // Dashboard
  { key: 'dashboard.firmwide', label: 'Firm-wide dashboard', description: 'Full dashboard view instead of the personal "my day" view',    group: 'dashboard', roles: TEAMLEAD_UP },
  { key: 'dashboard.funnel',   label: 'Proposal funnel',     description: 'See the proposal funnel section on the dashboard',             group: 'dashboard', roles: MANAGER_UP },
  { key: 'dashboard.configure', label: 'Configure dashboards', description: 'Create, preview and publish role dashboard templates',       group: 'dashboard', roles: PRIVILEGED },

  // Notes
  { key: 'notes.edit_others', label: "Edit others' notes", description: "Edit and delete notes authored by other users",                  group: 'notes', roles: TEAMLEAD_UP },

  // Meetings
  { key: 'meetings.view',     label: 'View meetings',       description: 'List and read meetings',                                       group: 'meetings', roles: ALL_STAFF },
  { key: 'meetings.create',   label: 'Create meetings',     description: 'Schedule meetings and invite attendees',                       group: 'meetings', roles: ALL_STAFF },
  { key: 'meetings.edit_all', label: 'Edit any meeting',    description: "Edit/cancel meetings organized by others (organizers always edit their own)", group: 'meetings', roles: MANAGER_UP },

  // Engagement templates
  { key: 'templates.view',   label: 'View templates',   description: 'List and read engagement templates, apply them to clients',       group: 'templates', roles: ALL_STAFF },
  { key: 'templates.manage', label: 'Manage templates', description: 'Create/edit/delete engagement templates and their items',         group: 'templates', roles: MANAGER_UP },

  // Catalogs & clauses (OPS 3.3)
  { key: 'clauses.manage', label: 'Manage clause library',  description: 'Create/edit/deactivate reusable proposal clauses',              group: 'catalogs', roles: MANAGER_UP },
  { key: 'catalog.manage', label: 'Manage business setup catalog', description: 'Create/edit free zones and their business-setup checklists',  group: 'catalogs', roles: MANAGER_UP },

  // Client legal documents (review gate; upload uses clients.edit)
  { key: 'documents.confirm', label: 'Confirm extracted documents', description: 'Confirm AI-extracted document data into the client record (review gate)', group: 'documents', roles: MANAGER_UP },

  // Reports (OPS 4.1)
  { key: 'reports.view', label: 'View reports', description: 'Open the Reports section (Client 360, staff productivity) and export them', group: 'reports', roles: MANAGER_UP },

  // Administration
  { key: 'admin.access', label: 'Admin Console access', description: 'Open the Admin Console and change role permissions',                group: 'admin', roles: PRIVILEGED },
  { key: 'trash.permanent_delete', label: 'Permanently delete from Trash', description: 'Hard-delete soft-deleted rows from the Admin Trash (restore only needs Admin access)', group: 'admin', roles: CEO_ONLY },
];

export type Capability = (typeof CAPABILITIES)[number]['key'];

const CAPABILITY_SET = new Set<string>(CAPABILITIES.map((c) => c.key));

export function isCapability(key: string): key is Capability {
  return CAPABILITY_SET.has(key);
}

function buildDefaultMatrix(): Record<UserRole, Record<string, boolean>> {
  const matrix = {} as Record<UserRole, Record<string, boolean>>;
  for (const role of USER_ROLES) {
    matrix[role] = {};
    for (const cap of CAPABILITIES) {
      matrix[role][cap.key] = (cap.roles as readonly string[]).includes(role);
    }
  }
  return matrix;
}

/** Today's behavior, encoded as data. CLIENT is all-false by construction. */
export const DEFAULT_ROLE_MATRIX: Record<UserRole, Record<string, boolean>> = buildDefaultMatrix();

export type RoleMatrix = Record<string, Record<string, boolean>>;

/** Pure evaluation used by the web UI (and as the resolver's fallback). */
export function canWithMatrix(role: UserRole, capability: string, matrix: RoleMatrix): boolean {
  return matrix[role]?.[capability] ?? false;
}

/** Overlay DB override rows onto the defaults to get the effective matrix. */
export function effectiveMatrix(overrides: Array<{ role: string; capability: string; allowed: boolean }>): RoleMatrix {
  const out: RoleMatrix = {};
  for (const role of USER_ROLES) out[role] = { ...DEFAULT_ROLE_MATRIX[role] };
  for (const o of overrides) {
    const roleRow = out[o.role];
    if (roleRow && isCapability(o.capability)) roleRow[o.capability] = o.allowed;
  }
  return out;
}
